import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ProposalPage } from './Proposal';
import { QuotationPage } from './Quotation';
import { useOfferSaves } from '../components/documents/offer-save';
import { useEditGuard } from '../lib/dirty';
import { apiGet } from '../lib/api-client';

export function SalesOfferPage({ side }: { side: 'proposal' | 'quotation' }) {
  const { id = '' } = useParams();
  const [doc, setDoc] = useState<'proposal' | 'quotation'>(side);
  const [view, setView] = useState<'document' | 'overview' | 'activity'>('document');
  const [pair, setPair] = useState<{ proposalId: string; quotationId: string } | null>(null);
  const [ready, setReady] = useState(false);
  const saves = useOfferSaves();

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setPair(null);
    setDoc(side);
    setView('document');
    async function load() {
      if (side === 'proposal') {
        const rows = await apiGet<{ id: string; kind?: string; proposalId?: string }[]>('/api/commercial');
        const quote = (rows || []).find((item) => item.kind === 'quotation' && item.proposalId === id);
        if (!cancelled) setPair(quote ? { proposalId: id, quotationId: quote.id } : null);
      } else {
        const rows = await apiGet<{ id: string; proposalId?: string }[]>('/api/quotations');
        const quote = (rows || []).find((item) => item.id === id);
        if (!cancelled) setPair(quote?.proposalId ? { proposalId: quote.proposalId, quotationId: id } : null);
      }
      if (!cancelled) setReady(true);
    }
    void load().catch(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, [id, side]);

  useEditGuard(pair ? `${pair.proposalId}:${pair.quotationId}` : '', JSON.stringify(saves.dirty), saves.saveAll, pair ? undefined : { silent: true });

  if (!ready) return <p className="text-sm text-[var(--bo-muted)]">Loading</p>;
  if (!pair) return side === 'proposal' ? <ProposalPage /> : <QuotationPage />;

  function select(next: string) {
    if (next === 'proposal' || next === 'quotation') {
      setDoc(next);
      setView('document');
      return;
    }
    if (next === 'overview' || next === 'activity') setView(next);
  }

  return (
    <>
      <div className={doc === 'proposal' ? '' : 'hidden'}>
        <ProposalPage
          recordId={pair.proposalId}
          paired
          saveAll={saves.saveAll}
          registerSave={saves.registerProposal}
          onDirty={saves.onProposalDirty}
          sectionTab={view === 'document' ? 'proposal' : view}
          onSelectTab={select}
        />
      </div>
      <div className={doc === 'quotation' ? '' : 'hidden'}>
        <QuotationPage
          recordId={pair.quotationId}
          paired
          saveAll={saves.saveAll}
          registerSave={saves.registerQuotation}
          onDirty={saves.onQuotationDirty}
          linkProposalId={saves.linkProposalId}
          sectionTab={view === 'document' ? 'quotation' : view}
          onSelectTab={select}
        />
      </div>
    </>
  );
}
